import React, { useCallback, useState } from 'react'

import { useCitizenAIAssistant } from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import { useLogAIChatDisabledOrgAttemptMutation } from 'src/fetch/aiChat'
import { Button, Input, T, VStack } from 'src/govwell-ui'
import { useSearchOrganizationsQuery } from 'src/pages/CitizenAIAssistantPage/use-search-organizations'
import {
  AIChatDisabledOrgAttemptSource,
  DisabledOrgAttemptSource,
  buildDisabledOrgMessage,
} from 'src/pages/CitizenAIAssistantPage/util'

const CitizenOrganizationSelector = () => {
  const context = useCitizenAIAssistant()
  const [searchQuery, setSearchQuery] = useState('')
  const { organizations, isLoading } = useSearchOrganizationsQuery(searchQuery)
  const setCurrentOrgContext = context?.setCurrentOrgContext
  const setMessages = context?.setMessages
  const addDeclinedOrgSlug = context?.addDeclinedOrgSlug
  const { mutateAsync: logDisabledOrgAttempt } = useLogAIChatDisabledOrgAttemptMutation()

  const handleSelectOrganization = useCallback(
    (slug: string, displayName: string, isAIChatEnabled: boolean) => {
      if (!isAIChatEnabled) {
        addDeclinedOrgSlug?.(slug)
        setMessages?.((prev) => [...prev, buildDisabledOrgMessage(displayName)])
        const source: AIChatDisabledOrgAttemptSource = DisabledOrgAttemptSource.JurisdictionSearch
        void logDisabledOrgAttempt({ input: { orgSlug: slug, source } }).catch(() => {})
        return
      }

      if (setCurrentOrgContext) {
        setCurrentOrgContext({
          orgSlug: slug,
          orgDisplayName: displayName,
        })
        context?.startNewConversation()
      }
    },
    [setCurrentOrgContext, context, addDeclinedOrgSlug, setMessages, logDisabledOrgAttempt]
  )

  return (
    <div className="mx-auto w-full max-w-md px-4 pb-4">
      <VStack gap={3} align="stretch">
        <T.BodySmall className="text-center text-neutral-500">
          Want answers specific to your organization (town, county, etc.)? Select your jurisdiction
          for details on local permits, fees, codes, and contacts.
        </T.BodySmall>
        <Input
          ariaLabel="Search for an organization"
          placeholder="Search for your organization..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          width="100%"
        />
        {isLoading && (
          <T.BodySmall className="text-center text-neutral-500">Searching...</T.BodySmall>
        )}
        {!isLoading && organizations.length > 0 && (
          <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
            {organizations.map((org) => (
              <Button
                key={org.slug}
                variant="default"
                onClick={() =>
                  handleSelectOrganization(org.slug, org.displayName, org.isAIChatEnabled)
                }
                className="text-left"
              >
                <T.Body>
                  {org.displayName}
                  {org.state && <span className="ml-1 text-neutral-500">({org.state})</span>}
                </T.Body>
              </Button>
            ))}
          </div>
        )}
        {!isLoading && searchQuery && organizations.length === 0 && (
          <T.BodySmall className="text-center text-neutral-500">
            No organizations found matching &ldquo;{searchQuery}&rdquo;
          </T.BodySmall>
        )}
      </VStack>
    </div>
  )
}

export default React.memo(CitizenOrganizationSelector)
